import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Loader2, Trash2, UserPlus, Users, ClipboardList, Send } from "lucide-react";
import { AppShell } from "@/components/app/AppShell";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { DatePicker } from "@/components/DatePicker";
import { MonthPicker } from "@/components/MonthPicker";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { formatDate } from "@/lib/format";

const METRIC_LABELS = {
  mrr: "MRR", arr: "ARR", cash_balance: "Cash balance", net_burn: "Net burn",
  runway_months: "Runway (months)", headcount: "Headcount", customers: "Customers",
  mom_growth_pct: "MoM growth %", churn_pct: "Churn %",
};

export default function Team() {
  const { company, user } = useAuth();
  const [team, setTeam] = useState(null);
  const [requests, setRequests] = useState([]);
  const [mine, setMine] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviteForm, setInviteForm] = useState({ name: "", email: "" });
  const [inviting, setInviting] = useState(false);
  const [reqDialog, setReqDialog] = useState(false);
  const [reqForm, setReqForm] = useState({ assignee_user_id: "", period: "", note: "", due_date: "", metrics: [] });
  const [creating, setCreating] = useState(false);
  const [submitFor, setSubmitFor] = useState(null);
  const [values, setValues] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    if (!company) return;
    try {
      const [t, r, m] = await Promise.all([
        api.get(`/companies/${company.id}/team`),
        api.get(`/companies/${company.id}/metric-requests`),
        api.get(`/metric-requests/mine`),
      ]);
      setTeam(t.data);
      setRequests(r.data);
      setMine(m.data);
    } catch (e) { console.error("Failed to load team", e); }
    finally { setLoading(false); }
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company]);

  const invite = async (e) => {
    e.preventDefault();
    setInviting(true);
    try {
      await api.post(`/companies/${company.id}/team/invites`, inviteForm);
      toast.success(`${inviteForm.name} invited. They register with this email to join your workspace.`);
      setInviteForm({ name: "", email: "" });
      load();
    } catch (err) { if (err?.response?.status !== 402) toast.error(err?.response?.data?.detail || "Could not invite"); }
    finally { setInviting(false); }
  };

  const cancelInvite = async (id) => {
    try { await api.delete(`/team-invites/${id}`); load(); }
    catch { toast.error("Could not cancel invite"); }
  };

  const removeMember = async (uid) => {
    try { await api.delete(`/companies/${company.id}/team/${uid}`); toast.success("Member removed"); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Could not remove member"); }
  };

  const createRequest = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      await api.post(`/companies/${company.id}/metric-requests`, reqForm);
      toast.success("Metric request sent");
      setReqDialog(false);
      setReqForm({ assignee_user_id: "", period: "", note: "", due_date: "", metrics: [] });
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not create request"); }
    finally { setCreating(false); }
  };

  const submitRequest = async () => {
    setSubmitting(true);
    try {
      await api.post(`/metric-requests/${submitFor.id}/submit`, { values });
      toast.success("Metrics submitted");
      setSubmitFor(null); setValues({});
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not submit"); }
    finally { setSubmitting(false); }
  };

  const deleteRequest = async (id) => {
    try { await api.delete(`/metric-requests/${id}`); load(); }
    catch { toast.error("Delete failed"); }
  };

  const toggleMetric = (key) => setReqForm((f) => ({
    ...f, metrics: f.metrics.includes(key) ? f.metrics.filter((m) => m !== key) : [...f.metrics, key],
  }));

  const myPending = mine.filter((r) => r.status === "pending");
  const assignable = (team?.members || []).filter((m) => m.user_id !== user?.id);

  return (
    <AppShell title="Team" subtitle="Workspace members and metric collection">
      {loading ? <p className="text-sm text-muted-foreground">Loading…</p> : (
        <Tabs defaultValue="members">
          <TabsList data-testid="team-tabs">
            <TabsTrigger value="members" data-testid="tab-members">Members</TabsTrigger>
            <TabsTrigger value="requests" data-testid="tab-requests">Metric requests {myPending.length > 0 ? `(${myPending.length})` : ""}</TabsTrigger>
          </TabsList>

          <TabsContent value="members" className="mt-4">
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-[360px_1fr]">
              <div className="space-y-4">
                <Card className="rounded-xl border bg-card p-4">
                  <div className="text-[11px] text-muted-foreground">Seats used</div>
                  <div className="h-display text-2xl font-semibold tabular-nums" data-testid="seats-used">{team?.seats_used} / {team?.seats}</div>
                  <p className="mt-1 text-xs text-muted-foreground">Included in your plan. Upgrade for more seats.</p>
                </Card>
                {team?.is_owner && (
                  <Card className="rounded-xl border bg-card p-5">
                    <div className="mb-3 flex items-center gap-2"><UserPlus className="h-4 w-4 text-primary" /><h2 className="text-sm font-semibold text-foreground">Invite a teammate</h2></div>
                    <form onSubmit={invite} className="space-y-3">
                      <div><Label>Name</Label><Input value={inviteForm.name} onChange={(e) => setInviteForm((f) => ({ ...f, name: e.target.value }))} required className="mt-1" data-testid="invite-name" /></div>
                      <div><Label>Email</Label><Input type="email" value={inviteForm.email} onChange={(e) => setInviteForm((f) => ({ ...f, email: e.target.value }))} required className="mt-1" data-testid="invite-email" /></div>
                      <Button type="submit" disabled={inviting} className="w-full gap-2" data-testid="invite-submit">
                        {inviting ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4" />} Send invite
                      </Button>
                    </form>
                    <p className="mt-3 text-xs text-muted-foreground">They create a founder account with this email and land directly in this workspace.</p>
                  </Card>
                )}
              </div>
              <div className="space-y-2">
                {(team?.members || []).map((m) => (
                  <Card key={m.user_id} className="flex items-center justify-between rounded-xl border bg-card p-4" data-testid={`member-row-${m.user_id}`}>
                    <div className="flex items-center gap-3">
                      <div className="flex h-10 w-10 items-center justify-center rounded-full bg-secondary text-sm font-semibold text-primary">{m.name?.charAt(0)?.toUpperCase()}</div>
                      <div>
                        <div className="text-sm font-semibold text-foreground">{m.name}</div>
                        <div className="text-xs text-muted-foreground">{m.email}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="secondary" className="capitalize">{m.team_role}</Badge>
                      {team?.is_owner && m.team_role !== "owner" && (
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-red-600" onClick={() => removeMember(m.user_id)} data-testid={`member-remove-${m.user_id}`}><Trash2 className="h-4 w-4" /></Button>
                      )}
                    </div>
                  </Card>
                ))}
                {(team?.invites || []).map((i) => (
                  <Card key={i.id} className="flex items-center justify-between rounded-xl border border-dashed bg-card p-4" data-testid={`invite-row-${i.id}`}>
                    <div>
                      <div className="text-sm font-semibold text-foreground">{i.name}</div>
                      <div className="text-xs text-muted-foreground">{i.email}</div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className="border-amber-200 bg-amber-50 text-amber-800">Invited</Badge>
                      {team?.is_owner && <Button variant="ghost" size="sm" className="text-xs text-muted-foreground" onClick={() => cancelInvite(i.id)} data-testid={`invite-cancel-${i.id}`}>Cancel</Button>}
                    </div>
                  </Card>
                ))}
                {(team?.members || []).length === 0 && (team?.invites || []).length === 0 && (
                  <Card className="rounded-xl border border-dashed p-8 text-center text-sm text-muted-foreground"><Users className="mx-auto mb-2 h-6 w-6" /> Just you so far.</Card>
                )}
              </div>
            </div>
          </TabsContent>

          <TabsContent value="requests" className="mt-4 space-y-6">
            {myPending.length > 0 && (
              <div>
                <h2 className="mb-2 text-sm font-semibold text-foreground">Assigned to you</h2>
                <div className="space-y-2">
                  {myPending.map((r) => (
                    <Card key={r.id} className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-primary/30 bg-primary/5 p-4" data-testid={`my-request-${r.id}`}>
                      <div>
                        <div className="text-sm font-semibold text-foreground">{r.requester_name} needs metrics for {r.period}</div>
                        <div className="text-xs text-muted-foreground">{r.metrics.map((m) => METRIC_LABELS[m]).join(", ")}{r.due_date ? ` · due ${r.due_date}` : ""}</div>
                        {r.note && <div className="mt-0.5 text-xs text-muted-foreground">"{r.note}"</div>}
                      </div>
                      <Button size="sm" className="gap-1.5" onClick={() => { setSubmitFor(r); setValues({}); }} data-testid={`my-request-submit-${r.id}`}><Send className="h-3.5 w-3.5" /> Submit values</Button>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            <div>
              <div className="mb-2 flex items-center justify-between">
                <h2 className="text-sm font-semibold text-foreground">All requests</h2>
                <Button size="sm" className="gap-1.5" onClick={() => setReqDialog(true)} disabled={assignable.length === 0} data-testid="request-create-btn"><Plus className="h-4 w-4" /> New request</Button>
              </div>
              {assignable.length === 0 && <p className="mb-2 text-xs text-muted-foreground">Invite teammates first to assign metric requests.</p>}
              {requests.length === 0 ? (
                <Card className="rounded-xl border border-dashed p-8 text-center text-sm text-muted-foreground"><ClipboardList className="mx-auto mb-2 h-6 w-6" /> No metric requests yet.</Card>
              ) : (
                <div className="space-y-2">
                  {requests.map((r) => (
                    <Card key={r.id} className="flex flex-wrap items-center justify-between gap-3 rounded-xl border bg-card p-4" data-testid={`request-row-${r.id}`}>
                      <div>
                        <div className="text-sm font-semibold text-foreground">{r.period} · {r.assignee_name}</div>
                        <div className="text-xs text-muted-foreground">{r.metrics.map((m) => METRIC_LABELS[m]).join(", ")} · {formatDate(r.created_at)}</div>
                      </div>
                      <div className="flex items-center gap-2">
                        {r.status === "submitted"
                          ? <Badge className="border-emerald-200 bg-emerald-50 text-emerald-700">Submitted</Badge>
                          : <Badge className="border-amber-200 bg-amber-50 text-amber-800">Pending</Badge>}
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-red-600" onClick={() => deleteRequest(r.id)} data-testid={`request-delete-${r.id}`}><Trash2 className="h-4 w-4" /></Button>
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      )}

      <Dialog open={reqDialog} onOpenChange={setReqDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Request metrics</DialogTitle></DialogHeader>
          <form onSubmit={createRequest} className="space-y-3">
            <div>
              <Label>Assign to</Label>
              <Select value={reqForm.assignee_user_id} onValueChange={(v) => setReqForm((f) => ({ ...f, assignee_user_id: v }))}>
                <SelectTrigger className="mt-1" data-testid="request-form-assignee"><SelectValue placeholder="Team member" /></SelectTrigger>
                <SelectContent>{assignable.map((m) => <SelectItem key={m.user_id} value={m.user_id}>{m.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Period</Label><MonthPicker value={reqForm.period} onChange={(v) => setReqForm((f) => ({ ...f, period: v }))} className="mt-1" data-testid="request-form-period" /></div>
              <div><Label>Due date</Label><DatePicker value={reqForm.due_date} onChange={(v) => setReqForm((f) => ({ ...f, due_date: v }))} className="mt-1" data-testid="request-form-due" /></div>
            </div>
            <div>
              <Label>Metrics</Label>
              <div className="mt-2 grid grid-cols-2 gap-2">
                {Object.entries(METRIC_LABELS).map(([k, label]) => (
                  <label key={k} className="flex items-center gap-2 text-xs text-foreground">
                    <Checkbox checked={reqForm.metrics.includes(k)} onCheckedChange={() => toggleMetric(k)} data-testid={`request-metric-${k}`} /> {label}
                  </label>
                ))}
              </div>
            </div>
            <div><Label>Note</Label><Input value={reqForm.note} onChange={(e) => setReqForm((f) => ({ ...f, note: e.target.value }))} className="mt-1" data-testid="request-form-note" /></div>
            <Button type="submit" disabled={creating || !reqForm.assignee_user_id || reqForm.metrics.length === 0} className="w-full gap-2" data-testid="request-form-save">
              {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send request
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!submitFor} onOpenChange={(v) => !v && setSubmitFor(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Submit metrics for {submitFor?.period}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {(submitFor?.metrics || []).map((m) => (
              <div key={m}>
                <Label>{METRIC_LABELS[m]}</Label>
                <Input type="number" step="any" value={values[m] ?? ""} onChange={(e) => setValues((v) => ({ ...v, [m]: e.target.value }))} className="mt-1" data-testid={`submit-value-${m}`} />
              </div>
            ))}
            <Button onClick={submitRequest} disabled={submitting} className="w-full gap-2" data-testid="submit-values-save">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Submit
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}
